import { UPDATE_PROFILE_STATUS, UPDATE_HAS_IDENTITY } from 'constants/actionTypes/auth';

const updateProfileStatus = profileStatus => dispatch => {
    dispatch({
        type: UPDATE_PROFILE_STATUS,
        payload: profileStatus
    });
};

const updateHasIdentity = data => dispatch => {
    dispatch({
        type: UPDATE_HAS_IDENTITY,
        payload: data
    });
};

/**
 * completeAuthentication (SIM-10 / ACS-4134): the single entry point that completes a
 * new /signin authentication by routing to the EXISTING per-method actions — it adds no
 * new API and mints no new token model (TD-006 / ADR-SIM10-002). It is a DISPATCHER,
 * not an API call, which is why it lives in AuthActions (services/api is API-only).
 *
 * Routing follows the backend contract (api-contracts/auth-service-apis.md):
 *   - 'otp':      verifyOTP is terminal — it returns access/refresh tokens + profile info
 *                 (same shape as generateToken). So we reuse MfaActions.validateOTP and hand
 *                 the token-shaped response straight to UserActions.signInSuccessAfterMFA
 *                 (the shared post-token handler: storeGenerateTokenValues + updateProfileStatus
 *                 + signInSuccess). No generateToken call.
 *   - 'password': login returns an intermediate token; UserActions.signIn already runs the
 *                 login -> generateTokens machinery internally. We just delegate to it.
 *
 * `securityToken` (+ requestOrigin 'OTPSignIn') is threaded through ONLY when the new flow
 * is enabled for this request (isNewSignInFlowEnabled — the SIM-1 two-layer gate mirror);
 * legacy MFA/login callers are untouched. The token is never logged (BR-021). This action
 * does NOT redirect — redirect + error routing stay in useAuthFlow (ADR-SIM10-002).
 *
 * @param {Object} params
 * @param {'otp'|'password'} params.method - Selected authentication method.
 * @param {string} [params.securityToken] - Intermediate token from authenticationMethods.
 * @param {string} [params.email] - Raw entered email. For the new OTP flow it is present ONLY
 *   for email-entered customers and OMITTED for phone customers (F-001 / ADR-SIM7-001); the
 *   caller (OtpEntryScreen) owns that decision and a masked/placeholder email is never sent.
 *   It is forwarded as-is to MfaActions.validateOTP (which drops it from the body when absent)
 *   and to signInSuccessAfterMFA. Password/legacy callers still pass a raw email unchanged.
 * @param {string} [params.otpCode] - Required when method === 'otp'.
 * @param {string} [params.mfaMode] - 'phone' | 'email' (OTP delivery mode, per the auth-service
 *   contract in services/api/authentication/mfaVerification.js); OTP only.
 * @param {boolean} [params.keepSignedIn] - "Stay signed in" selection.
 * @param {string} [params.password] - Required when method === 'password' AND no `response`
 *   is supplied (the thunk performs the login itself via UserActions.signIn — legacy callers).
 * @param {Object} [params.response] - Password branch (SIM-8 / ACS-4132): an ALREADY-obtained,
 *   token-shaped login result (authenticationApi.login resolves `{ ...generateTokensResponse,
 *   hasIdentity }` — login.js:240-243). When present, the caller has already performed the
 *   single terminal login (it needed the raw response to detect risk step-up), so this thunk
 *   MUST NOT log in again — it completes the session from that response via signInSuccessAfterMFA
 *   (the SAME shared post-token completer the OTP branch uses). Mirrors the OTP branch, whose
 *   verifyOTP is likewise terminal. `password` is ignored when `response` is present.
 * @param {Object} [params.otpSuccessArgs] - Extra args forwarded to signInSuccessAfterMFA
 *   (biAccountInfo, analyticsData, anonymousToken, hasIdentity, extraParams).
 * @param {Object} [params.passwordArgs] - Extra args forwarded to UserActions.signIn (legacy
 *   login-here path only, i.e. when `response` is absent).
 * @returns {Function} Redux thunk resolving with the auth response.
 */
export const completeAuthentication = params => async dispatch => {
    const {
        method,
        securityToken,
        email,
        otpCode,
        mfaMode,
        keepSignedIn,
        password,
        response: passwordResponse,
        otpSuccessArgs = {},
        passwordArgs = {}
    } = params;

    // Lazy dynamic imports (not top-level static imports) to avoid a circular-import init
    // hazard: utils/Authentication imports AuthActions at module top, and mfaActions ->
    // mfaVerification -> ufeApi -> newAuthUfeApi re-enters authenticationUtils before it
    // finishes evaluating. Importing here (at dispatch time) means every module is fully
    // initialized first. This mirrors the dynamic-import pattern utils/Authentication itself
    // uses (ES dynamic import, not a CommonJS call, so the pre-commit import rule is satisfied).
    const [{ default: MfaActions }, { default: UserActions }, { default: authenticationUtils }] = await Promise.all([
        import('actions/mfaActions'),
        import('actions/UserActions'),
        import('utils/Authentication')
    ]);

    const isNewFlow = authenticationUtils.isNewSignInFlowEnabled();

    if (method === 'otp') {
        // Enhancement to the existing verifyOTP API: add securityToken + requestOrigin only for the new flow.
        const otpOptions = isNewFlow && securityToken ? { securityToken, requestOrigin: 'OTPSignIn' } : {};

        // Suppress the GLOBAL white interstice for the new /signin flow: SignIn.f.jsx
        // already shows its own dark-scrim circular loader (FIX 3), so toggling the
        // interstice too would stack BOTH loaders. Legacy MFA verify keeps the interstice.
        const response = await dispatch(MfaActions.validateOTP(email, otpCode, mfaMode, keepSignedIn, otpOptions, isNewFlow));

        const {
            successCallback, biAccountInfo, analyticsData, anonymousToken, hasIdentity, extraParams
        } = otpSuccessArgs;

        // verifyOTP response is already token-shaped — reuse the shared post-token completion handler.
        dispatch(
            UserActions.signInSuccessAfterMFA(
                response,
                email,
                keepSignedIn,
                successCallback,
                biAccountInfo,
                analyticsData,
                anonymousToken,
                hasIdentity,
                extraParams
            )
        );

        return response;
    }

    // Password branch — complete-from-response (SIM-8 / ACS-4132, new /signin flow):
    // the caller (AuthorizationScreen) already performed the ONE terminal login inline
    // (it needs the raw response to detect risk step-up before completion), and login()
    // is token-shaped just like verifyOTP. So DO NOT log in again — reuse the same shared
    // post-token completer the OTP branch uses (storeGenerateTokenValues + updateProfileStatus
    // + signInSuccess). This is what fixes the double-login: signIn() below would call
    // authenticationApi.login() a second time (with undefined credentials when the response
    // was spread as params).
    if (passwordResponse) {
        const {
            successCallback, biAccountInfo, analyticsData, anonymousToken, hasIdentity, extraParams
        } = otpSuccessArgs;

        dispatch(
            UserActions.signInSuccessAfterMFA(
                passwordResponse,
                email,
                keepSignedIn,
                successCallback,
                biAccountInfo,
                analyticsData,
                anonymousToken,
                // Prefer the explicit hasIdentity arg; else fall back to the flag the
                // login response already carries (login.js returns `hasIdentity`).
                hasIdentity ?? passwordResponse.hasIdentity,
                extraParams
            )
        );

        return passwordResponse;
    }

    // Legacy password path (no pre-obtained response): enhancement to the existing login API —
    // securityToken rides in extraParams for the new flow; UserActions.signIn's
    // login -> generateTokens machinery is otherwise unchanged.
    const passwordExtraParams = isNewFlow && securityToken ? { ...(passwordArgs.extraParams || {}), securityToken } : passwordArgs.extraParams;

    return dispatch(
        UserActions.signIn(
            email,
            password,
            keepSignedIn,
            passwordArgs.loginForCheckout,
            passwordArgs.successCallback,
            passwordArgs.failureCallback,
            passwordArgs.isOrderConfirmation,
            passwordArgs.biAccountInfo,
            passwordArgs.isTestNTarget,
            passwordArgs.isSignInWithMessaging,
            passwordArgs.analyticsData,
            passwordExtraParams
        )
    );
};

// NOTE: completeAuthentication (SIM-10) is a NAMED export above, deliberately NOT part of this
// default object — per UI code-review.md the `export default {}` pattern is discouraged for new code.
// The default object is retained unchanged for the pre-existing shared consumers of
// updateProfileStatus/updateHasIdentity (ufeApi, Store, refreshToken, etc.).
export default { updateProfileStatus, updateHasIdentity };
